import type {
    ODataQueryOptions,
    ODataResponse,
    Runs,
} from '@experiences/interfaces';
import { ProcessType } from '@experiences/interfaces';
import {
    get,
    getOrchestratorODataRoute,
} from '@experiences/util';
import buildQuery from 'odata-query';

import { getCurrentOrchestratorUser } from './getCurrentOrchestratorUser';

const MAX_RUNS_COUNT = 8;
const PROCESS_MANDATORY_PERMISSIONS = [ 'Processes.View', 'Jobs.View', 'Jobs.Create' ];

interface IJob {
    Id: number;
    ReleaseName: string;
    StartTime: Date;
    State: string;
    CreationTime: Date;
    OrganizationUnitFullyQualifiedName: string;
    Release: {
        Id: number;
        ProcessType: string;
    };
}

function getPermissionQuery(): string {
    return PROCESS_MANDATORY_PERMISSIONS
        .map(permission => `&mandatoryPermissions=${permission}`)
        .join('');
}

export const processesUrl = `/api/orchestrator/processes`;

export async function getProcessesRecentRuns(
    accessToken: string,
    tenantName: string,
    accountLogicalName: string,
) {
    const user = await getCurrentOrchestratorUser(accessToken, accountLogicalName, tenantName);

    const baseUrl = `${getOrchestratorODataRoute(accountLogicalName, tenantName)}/`;
    const queryParam: ODataQueryOptions = {
        select: [ 'Id', 'ReleaseName', 'StartTime', 'State', 'CreationTime', 'OrganizationUnitFullyQualifiedName', 'Release' ],
        top: MAX_RUNS_COUNT,
        count: true,
        expand: [ 'Release($select=Id,ProcessType)' ],
        orderBy: [ 'CreationTime desc' ],
        filter: { and: [ { Reference: { eq: `${user.Id}` } } ] },
    };
    const permissionQuery = getPermissionQuery();
    const url = `${baseUrl}Jobs${buildQuery(queryParam)}${permissionQuery}`;

    const response = await get<ODataResponse<IJob>>(url, {
        accessToken,
        ignoreBasePath: true,
    });

    const recentRuns: Runs[] = (response.value ?? []).map((job: IJob) => ({
        Id: job.Id,
        Name: job.ReleaseName,
        StartDate: job.StartTime ?? job.CreationTime,
        OrganizationUnitFullyQualifiedName: job.OrganizationUnitFullyQualifiedName,
        Status: job.State,
        Type: ProcessType.Process,
        ProcessId: job.Release.Id,
        ProcessType: job.Release.ProcessType,
    }));

    return recentRuns
        .sort((a, b) => new Date(b.StartDate).getTime() - new Date(a.StartDate).getTime())
        .slice(0, MAX_RUNS_COUNT);
}
